/**
 * Voice journal (docs/voiceFeature.md, MVP) — mirrors src/types/capture.ts of the
 * API (DTOs, payloads and enums; the DB row types stay on the server).
 */

// ─── Enums ───────────────────────────────────────────────────────────────────

/**
 * queued       → uploaded, transcription not started yet
 * transcribing → Amazon Transcribe job running
 * transcribed  → transcript known, analysis waiting (e.g. no AI credits left)
 * analyzing    → the one AI analysis call is running
 * review       → suggestions waiting in "To review" (also after a partial save)
 * done         → nothing left to review (all saved or discarded)
 * failed       → transcription or analysis failed (error_code says which)
 */
export const CAPTURE_STATUSES = ['queued', 'transcribing', 'transcribed', 'analyzing', 'review', 'done', 'failed'] as const;
export type CaptureStatus = (typeof CAPTURE_STATUSES)[number];

/** Where a capture came from. Only 'app' in the MVP; channels follow in Release 2. */
export const CAPTURE_SOURCES = ['app'] as const;
export type CaptureSource = (typeof CAPTURE_SOURCES)[number];

/** 'ai' = transcript + AI analysis (suggestions); 'transcript' = text only, no AI (one journal entry). */
export const ANALYSIS_MODES = ['ai', 'transcript'] as const;
export type AnalysisMode = (typeof ANALYSIS_MODES)[number];

export const CAPTURE_ERROR_CODES = [
  'TRANSCRIPTION_FAILED',
  'ANALYSIS_FAILED',
  'AUDIO_EXPIRED',
  'NO_SPEECH',
] as const;
export type CaptureErrorCode = (typeof CAPTURE_ERROR_CODES)[number];

/** Suggestion kinds: the MVP's plus the UC-4 hand-off to livestock, incidents, the budget, restocking and dosing-plan changes. */
export const SUGGESTION_KINDS = [
  'journal_entry',
  'task_create',
  'task_recurring',
  'task_complete',
  'measurement',
  'water_change',
  'maintenance',
  'dosing',
  'livestock_added',
  'livestock_lost',
  'incident',
  'expense',
  'restock',
  'dosing_change',
] as const;
export type SuggestionKind = (typeof SUGGESTION_KINDS)[number];

/** Task kinds (a typed entry also produces measurements, see ENTRY_SUGGESTION_KINDS). */
export const TASK_SUGGESTION_KINDS = ['task_create', 'task_recurring', 'task_complete'] as const satisfies readonly SuggestionKind[];
export type TaskSuggestionKind = (typeof TASK_SUGGESTION_KINDS)[number];

/** Kinds a typed journal entry can produce (UC-B): follow-up tasks and the water-test results written in it. */
export const ENTRY_SUGGESTION_KINDS = [...TASK_SUGGESTION_KINDS, 'measurement'] as const satisfies readonly SuggestionKind[];

/** Same values as ai_task_suggestions.status. */
export const SUGGESTION_STATUSES = ['suggested', 'accepted', 'dismissed', 'expired'] as const;
export type SuggestionStatus = (typeof SUGGESTION_STATUSES)[number];

export type SuggestionConfidence = 'high' | 'medium' | 'low';

/**
 * Why a suggestion is marked "Please check" (unchecked by default):
 * - low_confidence      the AI was unsure
 * - number_mismatch     the deterministic number parser reads another value at the span
 * - implausible         outside the test kit's / parameter's plausible range
 * - low_audio_confidence Transcribe heard the number with confidence < 0.8
 * - unit_unknown        the unit is not accepted for the parameter
 * - multiple_matches    task_complete: several open tasks match — choose one
 * - no_match            task_complete / dosing: nothing in the plan matches
 * - unit_mismatch       dosing: another unit than the plan's
 * - currency_mismatch   expense: spoken in another currency than the user's budget currency (never converted)
 */
export const REVIEW_REASONS = [
  'low_confidence',
  'number_mismatch',
  'implausible',
  'low_audio_confidence',
  'unit_unknown',
  'multiple_matches',
  'no_match',
  'unit_mismatch',
  'currency_mismatch',
] as const;
export type ReviewReason = (typeof REVIEW_REASONS)[number];

export const TASK_CATEGORIES_FOR_SUGGESTIONS = ['water_testing', 'maintenance', 'observation', 'cleaning', 'equipment', 'custom'] as const;
export type SuggestionTaskCategory = (typeof TASK_CATEGORIES_FOR_SUGGESTIONS)[number];

export const RECURRENCE_FREQUENCIES = ['daily', 'every_x_days', 'weekly', 'specific_weekdays', 'monthly'] as const;
export type SuggestionRecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];

// ─── Payloads (one per kind) ─────────────────────────────────────────────────

/** The cleaned-up journal draft: no filler words, no dictated punctuation, paragraphs split by a blank line. */
export interface JournalEntryPayload {
  title: string;
  text: string;
}

/** A one-off task. dueDate = entry/recording date + offset, in the app time zone. */
export interface TaskCreatePayload {
  title: string;
  dueDate: string | null; // YYYY-MM-DD
  category: SuggestionTaskCategory;
}

/** Same JSON as tasks.recurrence_rule (services/taskOccurrences.ts) — not an RRULE. */
export interface SuggestionRecurrence {
  frequency: SuggestionRecurrenceFrequency;
  /** every_x_days: number of days; weekly/monthly: every n weeks/months (default 1). */
  interval?: number;
  /** specific_weekdays: 0 = Sunday … 6 = Saturday. */
  weekdays?: number[];
}

export interface TaskRecurringPayload {
  title: string;
  startDate: string; // YYYY-MM-DD, first occurrence
  recurrence: SuggestionRecurrence;
  category: SuggestionTaskCategory;
}

export interface TaskCandidate {
  /** Occurrence ref (`<taskId>_<YYYY-MM-DD>`) of a recurring task, or the task id of a one-off task. */
  ref: string;
  title: string;
  dueDate: string | null;
}

/** Mark an open task as done. taskRef null → the user has to choose one of the candidates. */
export interface TaskCompletePayload {
  title: string;
  taskRef: string | null;
  candidates: TaskCandidate[];
}

export interface LastValue {
  value: number;
  unit: string;
  measuredAt: string;
}

export interface MeasurementPayload {
  parameterId: string;
  parameterName: string;
  value: number;
  unit: string;
  /** Last stored value of this parameter (shown as "Last: … · n days ago"). */
  lastValue: LastValue | null;
  plausibleMin: number | null;
  plausibleMax: number | null;
}

export interface WaterChangePayload {
  volumeLiters: number | null;
  note: string | null;
}

export interface MaintenancePayload {
  note: string;
}

export interface DosingCandidate {
  scheduleId: string;
  productName: string;
  unit: string;
}

/** A dose of a product from the tank's own dosing plan (Persona review: MVP). */
export interface DosingPayload {
  scheduleId: string | null;
  productName: string;
  amount: number;
  unit: string;
  candidates: DosingCandidate[];
}

/** Livestock categories a voice note can add (same values as livestock.category). */
export const LIVESTOCK_CATEGORIES_FOR_SUGGESTIONS = ['fish', 'coral', 'invertebrate', 'other'] as const;
export type SuggestionLivestockCategory = (typeof LIVESTOCK_CATEGORIES_FOR_SUGGESTIONS)[number];

/** Same values as the budget's expense categories (services/budget/budgetSummary.ts). */
export const BUDGET_CATEGORIES_FOR_SUGGESTIONS = [
  'salt',
  'food',
  'filter_media',
  'supplements',
  'electricity',
  'water',
  'livestock',
  'equipment',
  'other',
] as const;
export type SuggestionBudgetCategory = (typeof BUDGET_CATEGORIES_FOR_SUGGESTIONS)[number];

/** UC-4: a new animal for the tank's livestock list (added on the recording date). New fish show a quarantine hint. */
export interface LivestockAddedPayload {
  commonName: string;
  category: SuggestionLivestockCategory;
}

export interface LivestockCandidate {
  livestockId: string;
  commonName: string;
  category: string;
}

/** UC-4: an animal of the tank died — marked deceased on the recording date, which opens an incident. */
export interface LivestockLostPayload {
  livestockId: string | null;
  commonName: string;
  candidates: LivestockCandidate[];
}

/** UC-4: a manual incident anchored at the recording time ("coral is bleaching"). */
export interface IncidentPayload {
  note: string;
}

/** UC-4: a budget expense in the user's budget currency ("bought salt for 45 euros"). */
export interface ExpensePayload {
  /** In major units (45.5 = 45,50); stored as amount × 100. */
  amount: number;
  /** The user's budget currency (users.currency) — shown, never converted. */
  currency: string;
  /** The currency that was spoken, when it was one (ISO code). */
  spokenCurrency: string | null;
  category: SuggestionBudgetCategory;
  note: string | null;
  spentOn: string; // YYYY-MM-DD
}

/** UC-4: something is running out ("salt is almost empty") — saved as a task (there is no inventory). */
export interface RestockPayload {
  /** What to buy, e.g. "Salt". */
  item: string;
  /** The task title, e.g. "Buy salt". */
  title: string;
  dueDate: string | null; // YYYY-MM-DD
}

export interface DosingChangeCandidate extends DosingCandidate {
  /** The plan's current dose per dosing (null when unknown). */
  currentAmount: number | null;
}

/**
 * UC-4: a lasting change of a dosing plan ("raised the KH dose to 30 ml"). Never
 * applied to the plan directly (dose guard, D10): saving creates a reminder task,
 * and the card links to the plan editor with the new dose filled in.
 */
export interface DosingChangePayload {
  scheduleId: string | null;
  productName: string;
  currentAmount: number | null;
  newAmount: number;
  unit: string;
  /** The task title, e.g. "Set KH dose to 30 ml". */
  title: string;
  candidates: DosingChangeCandidate[];
}

export interface SuggestionPayloadByKind {
  journal_entry: JournalEntryPayload;
  task_create: TaskCreatePayload;
  task_recurring: TaskRecurringPayload;
  task_complete: TaskCompletePayload;
  measurement: MeasurementPayload;
  water_change: WaterChangePayload;
  maintenance: MaintenancePayload;
  dosing: DosingPayload;
  livestock_added: LivestockAddedPayload;
  livestock_lost: LivestockLostPayload;
  incident: IncidentPayload;
  expense: ExpensePayload;
  restock: RestockPayload;
  dosing_change: DosingChangePayload;
}

export type SuggestionPayload = SuggestionPayloadByKind[SuggestionKind];

// ─── API shapes ──────────────────────────────────────────────────────────────

export interface CaptureSuggestionDto<K extends SuggestionKind = SuggestionKind> {
  id: string;
  kind: K;
  status: SuggestionStatus;
  confidence: SuggestionConfidence;
  /** true → "Please check": the row starts unchecked. */
  needsReview: boolean;
  reviewReasons: ReviewReason[];
  /** Character offsets of the phrase in the capture's transcript (or the entry's plain text). */
  spanStart: number | null;
  spanEnd: number | null;
  /** The phrase itself, e.g. "KH in drei Tagen nachmessen". */
  phrase: string | null;
  payload: SuggestionPayloadByKind[K];
  resultType: string | null;
  resultId: string | null;
}

/** UC-7: rule-based feedback on a measurement suggestion, computed from stored data only. */
export type ResultFeedbackKind = 'below_target' | 'above_target' | 'in_range' | 'stale' | 'no_previous';

export interface ResultFeedbackDto {
  suggestionId: string;
  parameterId: string;
  parameterName: string;
  kind: ResultFeedbackKind;
  value: number;
  unit: string;
  targetMin: number | null;
  targetMax: number | null;
  lastValue: number | null;
  lastMeasuredAt: string | null;
  daysSinceLast: number | null;
  /** i18n key of a calm next step (below/above target), e.g. 'voice.feedback.next.retestBeforeCorrecting'. */
  nextStepKey: string | null;
}

export interface CaptureDto {
  id: string;
  aquariumId: string;
  aquariumName: string;
  /** Tank the AI recognised in the note (UC-5); the user confirms it on the card. */
  detectedAquariumId: string | null;
  detectedPhrase: string | null;
  status: CaptureStatus;
  source: CaptureSource;
  analysisMode: AnalysisMode;
  /** UC-18: the note describes an acute emergency (e.g. 'fish_distress') — the card offers the emergency help. */
  emergencySituation: string | null;
  errorCode: CaptureErrorCode | null;
  /** Analysis waits for AI credits (status 'transcribed'). */
  waitingForCredits: boolean;
  recordedAt: string;
  createdAt: string;
  processedAt: string | null;
  appliedAt: string | null;
  durationMs: number | null;
  language: string;
  /** Effective transcript (the user's edit, else Transcribe's text). Null until transcribed. */
  transcript: string | null;
  transcriptEdited: boolean;
  suggestions: CaptureSuggestionDto[];
  feedback: ResultFeedbackDto[];
  /** Suggestions saved from this capture. */
  savedCount: number;
  /** Undo of the saved items is possible until then (24 h after the last save). */
  undoableUntil: string | null;
  /** Credits the next "Re-analyse" costs (0 = included). */
  reanalysisCredits: number;
}

/** GET /api/v1/captures/summary — counts for the tab bar, sidebar and the "Note ready" banner. */
export interface CaptureSummaryDto {
  toReview: number;
  processing: number;
  failed: number;
  /** Captures that reached 'review' or 'failed' in the last 24 h (newest first, max 20). */
  recent: Array<{ id: string; status: CaptureStatus; recordedAt: string; itemCount: number; aquariumId: string }>;
}

export interface VoicePricingDto {
  credits: number;
  shortCredits: number;
  shortMaxSeconds: number;
  introCredits: number;
  introCount: number;
  introUsed: number;
  introLeft: number;
  /** "Transcription only, no AI": flat price per note. */
  transcriptOnlyCredits: number;
  /** Guided test session with spoken values: flat price per session (UC-15). */
  guidedSessionCredits: number;
}

/** GET /api/v1/me/voice */
export interface VoiceSettingsDto {
  /** VOICE_CAPTURE_ENABLED — voice notes can be recorded (typed-entry task detection works regardless). */
  enabled: boolean;
  consent: { given: boolean; version: string | null; currentVersion: string; givenAt: string | null };
  pricing: VoicePricingDto;
  maxDurationSec: number;
  maxBytes: number;
}

/** POST /api/v1/captures/:id/apply and the journal task-suggestions apply route. */
export interface ApplyItem {
  id: string;
  /** Edited payload (same shape as the suggestion's); omitted → the suggested payload. */
  payload?: Record<string, unknown>;
}

export interface ApplyRequest {
  /** The confirmed tank (captures only; must be one of the user's own). */
  aquariumId?: string;
  items: ApplyItem[];
  /** Suggestions to dismiss in the same step (never shown again). */
  dismiss?: string[];
}

export interface ApplyResultItem {
  suggestionId: string;
  resultType: string;
  resultId: string;
}
